import { useState, useCallback, useMemo } from 'react';
import { useGetServiceUsersQuery, type DashboardOptions } from '@/entities/dashboard';

import type { FormattedStats } from './types';

export function useServiceUsers() {
  const [options, setOptions] = useState<DashboardOptions>({
    metric: 'W',
    period: 'L6M',
    partners: [],
    locations: []
  });

  const { data, isLoading, error } = useGetServiceUsersQuery(options);

  const updateOption = useCallback((key: keyof DashboardOptions, value: string) => {
    setOptions((prev) => ({ ...prev, [key]: value }));
  }, []);

  const formatValue = useCallback((value: number, metric?: string): string => {
    if (metric === 'W') {
      return `${(value / 1000).toFixed(1)} mt`;
    }
    if (metric === 'V') {
      return `${value.toFixed(1)} m³`;
    }
    if (metric === 'VA') {
      return `$${value.toLocaleString()}`;
    }
    if (metric === 'C') {
      return value.toLocaleString();
    }
    return value.toLocaleString();
  }, []);

  const formattedStats: FormattedStats | null = useMemo(() => {
    if (!data) return null;

    const netBalance = Math.abs(data.totalReceived - data.totalReleased);
    const utilizationRate =
      data.totalReceived > 0 ? ((data.totalReleased / data.totalReceived) * 100).toFixed(1) : '0.0';

    return {
      totalReceived: formatValue(Math.abs(data.totalReceived), options.metric),
      totalReleased: formatValue(Math.abs(data.totalReleased), options.metric),
      servicePartners: data.serviceUsers.categories.length.toString(),
      netBalance: formatValue(netBalance, options.metric),
      netBalancePositive: data.totalReceived > data.totalReleased,
      utilizationRate: `${utilizationRate}%`,
      activePartners: data.serviceUsers.categories.length,
      productTypes: data.topProducts.series.length
    };
  }, [data, options.metric, formatValue]);

  return {
    data,
    formattedStats,
    isLoading,
    error,
    options,
    updateOption
  };
}
